import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  ColombiaRail,
  ColombiaService,
  QuoteResult,
} from '../../services/colombia.service';

export interface ClientSimData {
  countryCode?: string;
  countryName?: string;
}

type Origen = 'USD' | 'CLP' | 'VES';

/** Info de la pata CLP→USD cuando el origen es CLP. */
interface ClpLeg {
  montoClp: number;
  tasaClpUsd: number;
  montoUsd: number;
  /** Tasa única que ve el cliente: COP por CLP. */
  tasaCopClp: number;
}

/** Info de la pata VES→USD cuando el origen es VES. */
interface VesLeg {
  montoVes: number;
  tasaVesUsd: number;
  montoUsd: number;
  /** Tasa única que ve el cliente: COP por VES. */
  tasaCopVes: number;
}

/** Mínimo del rail (USD). */
const MIN_USD = 20;

/**
 * MODAL "Vista cliente" — simula la cotización como la vería el cliente
 * (segmento cliente) + el desglose interno (segmento Aguacate). Soporta origen
 * **USD** (saldo dólar) o **CLP** (saldo pesos): en CLP convierte a USD a la
 * tasa de venta configurada y reusa el mismo motor USD→COP (Vita, ESPEJO).
 * NO expone "Vita" en el segmento cliente (feedback_no_vita_in_client_ui).
 */
@Component({
  selector: 'app-rail-colombia-client-sim',
  templateUrl: './rail-colombia-client-sim.component.html',
  styleUrls: ['./rail-colombia-client-sim.component.sass'],
})
export class RailColombiaClientSimComponent {
  readonly rails: { code: ColombiaRail; label: string }[] = [
    { code: 'COBREB', label: 'Instantáneo (Bre-B)' },
    { code: 'CO', label: 'Transferencia bancaria (1 día)' },
  ];
  readonly origenes: { code: Origen; label: string }[] = [
    { code: 'USD', label: 'USD (saldo dólar)' },
    { code: 'CLP', label: 'CLP (saldo pesos)' },
    { code: 'VES', label: 'VES (saldo bolívares)' },
  ];

  origen: Origen = 'USD';
  rail: ColombiaRail = 'COBREB';
  monto = 100;

  loading = false;
  result: QuoteResult | null = null;
  clp: ClpLeg | null = null;
  clpActivo = true;
  ves: VesLeg | null = null;
  vesActivo = true;
  bajoMinimo = false;
  error: string | null = null;

  constructor(
    private svc: ColombiaService,
    public dialogRef: MatDialogRef<RailColombiaClientSimComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ClientSimData,
  ) {}

  /** Al cambiar de origen, reseteo el monto a un default razonable de su unidad. */
  onOrigenChange(): void {
    this.monto = this.origen === 'CLP' ? 100000 : this.origen === 'VES' ? 100000 : 100;
    this.result = null;
    this.clp = null;
    this.ves = null;
    this.error = null;
  }

  async cotizar(): Promise<void> {
    this.loading = true;
    this.error = null;
    this.result = null;
    this.clp = null;
    this.ves = null;
    this.bajoMinimo = false;
    try {
      let montoUsd: number;
      if (this.origen === 'CLP') {
        const live = await this.svc.getClpLive();
        const tasa = live?.rate?.efectiva ?? 0;
        this.clpActivo = !!live?.activo;
        if (!(tasa > 0)) {
          this.error = 'CLP→COP no está configurado (sin tasa). Configuralo en “CLP/COP”.';
          return;
        }
        montoUsd = Number(this.monto) / tasa;
        this.clp = {
          montoClp: Number(this.monto),
          tasaClpUsd: tasa,
          montoUsd,
          tasaCopClp: 0,
        };
      } else if (this.origen === 'VES') {
        const live = await this.svc.getVesLive();
        const tasa = live?.rate?.efectiva ?? 0;
        this.vesActivo = !!live?.activo;
        if (!(tasa > 0)) {
          this.error = 'VES→COP no está configurado (sin tasa). Configuralo en “VES/COP”.';
          return;
        }
        montoUsd = Number(this.monto) / tasa;
        this.ves = {
          montoVes: Number(this.monto),
          tasaVesUsd: tasa,
          montoUsd,
          tasaCopVes: 0,
        };
      } else {
        montoUsd = Number(this.monto);
      }
      this.bajoMinimo = montoUsd < MIN_USD;
      const q = await this.svc.quote(this.rail, montoUsd);
      this.result = q;
      if (this.clp && q.resultado && this.clp.montoClp > 0) {
        this.clp.tasaCopClp = q.resultado.recibeCop / this.clp.montoClp;
      }
      if (this.ves && q.resultado && this.ves.montoVes > 0) {
        this.ves.tasaCopVes = q.resultado.recibeCop / this.ves.montoVes;
      }
    } catch (e: any) {
      this.error = 'No se pudo cotizar en este momento.';
    } finally {
      this.loading = false;
    }
  }

  get esClp(): boolean {
    return this.origen === 'CLP';
  }
  get esVes(): boolean {
    return this.origen === 'VES';
  }

  /** Lo que el cliente envía, en su moneda de origen. */
  get montoEnviado(): number | null {
    if (this.esClp) return this.clp?.montoClp ?? null;
    if (this.esVes) return this.ves?.montoVes ?? null;
    return this.result?.resultado?.montoUsd ?? null;
  }

  /** Tasa que ve el cliente (COP por CLP/VES si ese origen; COP por USD si USD). */
  get tasaClienteValor(): number | null {
    if (this.esClp) return this.clp?.tasaCopClp ?? null;
    if (this.esVes) return this.ves?.tasaCopVes ?? null;
    const r = this.result?.resultado;
    return r && r.montoUsd > 0 ? r.recibeCop / r.montoUsd : null;
  }
  /** digitsInfo del number pipe según la unidad (COP/CLP y COP/VES llevan decimales). */
  get tasaClienteFmt(): string {
    if (this.esVes) return '1.4-4';
    return this.esClp ? '1.3-3' : '1.0-0';
  }

  /** Plazo legible para el cliente (sin jerga interna). */
  get plazoLabel(): string {
    const d = this.result?.businessDays;
    if (d == null) return '';
    if (d <= 0) return 'menos de 1 hora';
    return d === 1 ? '1 día hábil' : `${d} días hábiles`;
  }

  get ofrecible(): boolean {
    return (
      !!this.result?.disponible &&
      this.result?.paisActivo !== false &&
      (!this.esClp || this.clpActivo) &&
      (!this.esVes || this.vesActivo)
    );
  }

  close(): void {
    this.dialogRef.close();
  }
}
